import {
  html,
  attr,
  Value,
  prop,
  When,
  on,
  Signal,
  computedOfAsync,
} from '@tempots/dom'
import { Anchor, NavigationService } from '@tempots/ui'
import { Button, Icon, InputWrapper, TextInput, Modal } from '@tempots/beatui'
import { PageLayout } from '@/components/layout'
import { StatusBadge, toast } from '@/components/ui'
import type { BadgeVariant } from '@/components/ui'
import { formatDate, formatCredits } from '@/utils/formatting'
import { apiKeys as apiKeysService } from '@/services/gateway'
import { Workspace } from '@/state/workspace'
import type { CreditLimitType } from '@/services/types'

function getApiKeyStatusVariant(status: string | undefined): BadgeVariant {
  return status === 'revoked' ? 'neutral' : 'success'
}

function getApiKeyStatusLabel(status: string | undefined): string {
  return status === 'revoked' ? 'Revoked' : 'Active'
}

export interface ApiKeyDetailPageOptions {
  projectId: Signal<string>
  id: Signal<string>
}

export function ApiKeyDetailPage({
  projectId,
  id: keyId,
}: ApiKeyDetailPageOptions) {
  projectId.on(Workspace.changeProject)

  const apiKey = computedOfAsync(projectId, keyId)(
    async (pid: string, kid: string) => await apiKeysService.get(pid, kid),
    null
  ).deriveProp()
  // const isLoading = prop(true)
  // const error = prop<string | null>(null)

  const name = apiKey.map((k) => k?.name ?? '').deriveProp()
  const creditLimitType = prop<CreditLimitType>('none')
  const creditLimit = prop<number | null>(null)
  const isSaving = prop(false)
  const saveError = prop<string | null>(null)

  const newlyCreatedKey = keyId
    .map((kid) => {
      const key = sessionStorage.getItem(`newApiKey_${kid}`)
      if (key) {
        sessionStorage.removeItem(`newApiKey_${kid}`)
        return key
      }
      return null
    })
    .deriveProp()

  async function handleSave() {
    const pid = projectId.value
    const kid = keyId.value

    if (!pid || !kid) {
      saveError.set('Missing project or key ID')
      return
    }

    isSaving.set(true)
    saveError.set(null)

    try {
      const updated = await apiKeysService.update(pid, kid, {
        name: name.value,
        creditLimitType: creditLimitType.value,
        creditLimit: creditLimit.value,
      })
      apiKey.set(updated)
      toast.success('API key updated successfully')
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update API key'
      saveError.set(message)
      toast.error(message)
    } finally {
      isSaving.set(false)
    }
  }

  async function handleRevoke(close: () => void) {
    const pid = projectId.value
    const kid = keyId.value

    if (!pid || !kid) return

    try {
      await apiKeysService.revoke(pid, kid)
      close()
      toast.success('API key revoked')
      NavigationService.navigate(Workspace.projectUrl('/api-keys', pid) ?? '#')
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to revoke API key'
      )
    }
  }

  return PageLayout({
    header: html.div(
      attr.class('flex items-center gap-3'),
      Anchor(
        {
          href: projectId.map(
            (pid) => Workspace.projectUrl('/api-keys', pid) ?? '#'
          ),
        },
        attr.class('text-gray-400 hover:text-gray-600'),
        Icon({ icon: 'mdi:chevron-left' }, attr.class('w-5 h-5'))
      ),
      apiKey.map((k) => k?.name || 'API Key')
    ),
    content: When(
      apiKey.map((k) => k !== null),
      () =>
        html.div(
          attr.class('space-y-6'),
          // Newly created key banner
          When(
            newlyCreatedKey.map((k) => k !== null),
            () =>
              html.div(
                attr.class(
                  'bg-green-50 border border-green-200 rounded-xl p-6'
                ),
                html.div(
                  attr.class('flex items-start gap-4'),
                  Icon(
                    { icon: 'mdi:check-circle' },
                    attr.class('w-6 h-6 text-green-600 flex-shrink-0')
                  ),
                  html.div(
                    attr.class('flex-1'),
                    html.h3(
                      attr.class('text-base font-semibold text-green-800 mb-1'),
                      'API Key Created Successfully!'
                    ),
                    html.p(
                      attr.class('text-sm text-green-700 mb-4'),
                      "Make sure to copy your API key now. You won't be able to see it again!"
                    ),
                    html.div(
                      attr.class('flex gap-2'),
                      html.code(
                        attr.class(
                          'flex-1 px-3 py-2 bg-white rounded-lg text-sm text-gray-900 font-mono break-all border border-green-200'
                        ),
                        newlyCreatedKey
                      ),
                      Button(
                        {
                          variant: 'outline',
                          onClick: async () => {
                            const key = newlyCreatedKey.get()
                            if (key) {
                              try {
                                await navigator.clipboard.writeText(key)
                                toast.success('API key copied to clipboard')
                              } catch {
                                toast.error('Failed to copy to clipboard')
                              }
                            }
                          },
                        },
                        Icon(
                          { icon: 'mdi:content-copy' },
                          attr.class('w-4 h-4 mr-2')
                        ),
                        'Copy'
                      )
                    )
                  ),
                  html.button(
                    on.click(() => newlyCreatedKey.set(null)),
                    attr.class('text-green-600 hover:text-green-800'),
                    Icon({ icon: 'mdi:close' }, attr.class('w-5 h-5'))
                  )
                )
              )
          ),
          // Key Info Card
          html.div(
            attr.class('bg-white rounded-xl border border-gray-200 p-6'),
            html.div(
              attr.class('flex items-center justify-between mb-6'),
              html.h2(
                attr.class('text-base font-semibold text-gray-900'),
                'Key Details'
              ),
              StatusBadge({
                label: apiKey.map((k) => getApiKeyStatusLabel(k?.status)),
                variant: apiKey.map((k) => getApiKeyStatusVariant(k?.status)),
              })
            ),
            html.div(
              attr.class('space-y-4'),
              // Name
              InputWrapper({
                label: 'Name',
                content: TextInput({
                  value: name,
                  onInput: (v) => name.set(v),
                }),
              }),
              // Key
              html.div(
                html.label(
                  attr.class('block text-sm font-medium text-gray-700 mb-1.5'),
                  'Key'
                ),
                html.div(
                  attr.class('flex items-center gap-2'),
                  html.code(
                    attr.class(
                      'flex-1 px-3 py-2 bg-gray-100 rounded-lg text-sm text-gray-600 font-mono'
                    ),
                    apiKey.map((k) => {
                      const prefix = k?.prefix || ''
                      // Mask the key with placeholder characters
                      return prefix ? `${prefix}${'•'.repeat(24)}` : ''
                    })
                  )
                ),
                html.p(
                  attr.class('mt-1 text-xs text-gray-500'),
                  'Full key is only shown once at creation'
                )
              ),
              // Scopes
              When(
                apiKey.map((k) => (k?.scopes?.length || 0) > 0),
                () =>
                  html.div(
                    html.label(
                      attr.class(
                        'block text-sm font-medium text-gray-700 mb-1.5'
                      ),
                      'Scopes'
                    ),
                    html.div(
                      attr.class('flex flex-wrap gap-2'),
                      apiKey.map((k) =>
                        k?.scopes
                          ? k.scopes
                              .map((scope) =>
                                html.span(
                                  attr.class(
                                    'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800'
                                  ),
                                  scope
                                )
                              )
                              .reduce(
                                (acc, el) => html.div(acc, el),
                                html.div()
                              )
                          : html.div()
                      )
                    )
                  )
              ),
              // Credit Limit Type
              InputWrapper({
                label: 'Credit Limit Type',
                description: 'Control how credits are limited for this API key',
                content: html.select(
                  attr.class(
                    'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
                  ),
                  attr.value(creditLimitType),
                  on.change((e) =>
                    creditLimitType.set(
                      (e.target as HTMLSelectElement).value as CreditLimitType
                    )
                  ),
                  html.option(attr.value('none'), 'No Limit'),
                  html.option(
                    attr.value('daily'),
                    'Daily Limit (resets at midnight UTC)'
                  ),
                  html.option(attr.value('total'), 'Total Limit (never resets)')
                ),
              }),
              // Credit Limit Amount (only shown when limit type is not 'none')
              When(
                creditLimitType.map((t) => t !== 'none'),
                () =>
                  InputWrapper({
                    label: 'Credit Limit Amount',
                    description: creditLimitType.map((t) =>
                      t === 'daily'
                        ? 'Maximum credits that can be used per day'
                        : 'Maximum total credits that can be used'
                    ),
                    content: TextInput({
                      value: creditLimit.map((v) => v?.toString() || ''),
                      onInput: (v) => {
                        const num = parseInt(v)
                        creditLimit.set(isNaN(num) ? null : num)
                      },
                      placeholder: 'Enter limit amount',
                    }),
                  })
              ),
              // Save button
              html.div(
                attr.class('pt-4 flex justify-end gap-2'),
                Button(
                  {
                    loading: isSaving,
                    onClick: handleSave,
                  },
                  'Save Changes'
                )
              )
            )
          ),
          // Usage Stats
          html.div(
            attr.class('bg-white rounded-xl border border-gray-200 p-6'),
            html.h2(
              attr.class('text-base font-semibold text-gray-900 mb-6'),
              'Usage Statistics'
            ),
            html.div(
              attr.class('grid grid-cols-1 sm:grid-cols-3 gap-6'),
              StatItem(
                'Credits Used',
                apiKey.map((k) =>
                  formatCredits(k?.creditsUsed ?? 0, { includeSuffix: false })
                )
              ),
              StatItem(
                'Credit Limit',
                apiKey.map((k) =>
                  k?.creditLimit
                    ? formatCredits(k.creditLimit, { includeSuffix: false })
                    : 'No limit'
                )
              ),
              StatItem(
                'Last Used',
                apiKey.map((k) =>
                  k?.lastUsedAt ? formatDate(k.lastUsedAt) : 'Never'
                )
              )
            )
          ),
          // Danger Zone
          html.div(
            attr.class('bg-white rounded-xl border border-red-200 p-6'),
            html.h2(
              attr.class('text-base font-semibold text-red-600 mb-2'),
              'Danger Zone'
            ),
            html.p(
              attr.class('text-sm text-gray-600 mb-4'),
              'Revoking this API key will immediately invalidate it. Any applications using this key will stop working.'
            ),
            Modal({ size: 'sm', dismissable: true }, (open, close) =>
              html.div(
                Button(
                  {
                    variant: 'outline',
                    onClick: () =>
                      open({
                        header: html.h2(
                          attr.class('text-lg font-semibold text-gray-900'),
                          'Revoke API Key'
                        ),
                        body: html.p(
                          attr.class('text-sm text-gray-600'),
                          'Are you sure you want to revoke this API key? This action cannot be undone.'
                        ),
                        footer: html.div(
                          attr.class('flex justify-end gap-3'),
                          Button(
                            { variant: 'outline', onClick: close },
                            'Cancel'
                          ),
                          Button(
                            {
                              onClick: () => handleRevoke(close),
                            },
                            html.span(
                              attr.class('text-red-600'),
                              'Revoke API Key'
                            )
                          )
                        ),
                      }),
                  },
                  html.span(attr.class('text-red-600'), 'Revoke API Key')
                )
              )
            )
          )
        )
    ),
  })
}

function StatItem(label: string, value: Value<string>) {
  return html.div(
    html.p(attr.class('text-sm text-gray-500 mb-1'), label),
    html.p(attr.class('text-2xl font-bold text-gray-900'), value)
  )
}
